// PipelineCard — markup LITERAL do wireframe §09 (varredura v2 §5.6-C3):
// card no padrão de Skills (.sklc, wf-skills.css) + switch .agt-sw na
// .pipe-sw-row (wf-pipes.css) + foot "<b>N</b> runs · <b>X%</b> sucesso ·
// <b>K</b> etapas" + kebab ⋮ (.skl-kebab) com ver detalhes · promover/
// instanciar · excluir.
//
// PIPE-01: SEM badge "requires business" e SEM diagrama DAG.
// PIPE-05: % sucesso = completos/(completos+falhos), cancelados FORA.
// GAP (reportado): backend não expõe DELETE /v1/pipelines/:id — item
// "Excluir pipeline" fica desabilitado ("em breve"), sem inventar.

import { Workflow, Eye, Globe, Copy, Trash2 } from 'lucide-react';
import type { PipelineSummary } from '@/lib/api-pipelines';
import type { RunCounts } from '@/lib/pipeline-counts';
import { fmtNumber } from '@/lib/format';
import { WfKebabMenu, type WfKebabEntry } from '@/components/ui/WfKebabMenu';
import { formatSuccessPct } from './pipeline-metrics';

export interface PipelineCardProps {
  pipeline: PipelineSummary;
  /** Agregado de runs deste pipeline (lib/pipeline-counts.aggregateRuns). */
  counts: RunCounts | undefined;
  /** Nº de etapas da versão ativa (fetch lazy do detalhe); null = indisponível. */
  stepsCount: number | null;
  onOpenDetail: () => void;
  /** Switch ativar/desativar — page faz o PATCH otimista + revert em erro. */
  onToggleActive: (next: boolean) => void;
  onPromote: () => void;
  onInstantiate: () => void;
}

export function PipelineCard({
  pipeline: p,
  counts,
  stepsCount,
  onOpenDetail,
  onToggleActive,
  onPromote,
  onInstantiate,
}: PipelineCardProps) {
  const totalRuns = counts?.total ?? 0;
  const isGlobal = p.scope_label === 'global';

  const menuItems: WfKebabEntry[] = [
    { key: 'details', label: 'Ver detalhes', icon: Eye, onSelect: onOpenDetail },
    isGlobal
      ? { key: 'instantiate', label: 'Instanciar no tenant', icon: Copy, onSelect: onInstantiate }
      : {
          key: 'promote',
          label: 'Promover para global',
          icon: Globe,
          onSelect: onPromote,
          disabled: !p.is_promotable,
          title: p.is_promotable ? undefined : 'Pipeline não promovível',
        },
    'sep',
    {
      key: 'delete',
      label: 'Excluir pipeline',
      icon: Trash2,
      danger: true,
      soon: true,
      title: 'Indisponível: backend ainda não expõe DELETE /v1/pipelines/:id (gap reportado)',
    },
  ];

  return (
    <div
      className="sklc"
      role="button"
      tabIndex={0}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button, a, label, [role="menu"]')) return;
        onOpenDetail();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpenDetail();
        }
      }}
      aria-label={`Pipeline ${p.name}`}
    >
      {/* Kebab ⋮ — canto superior direito (wireframe .skl-kebab) */}
      <WfKebabMenu ariaLabel={`Ações do pipeline ${p.slug}`} items={menuItems} />

      {/* Header: ícone + nome + slug + badges versão · scope (wireframe .sklc-h) */}
      <div className="sklc-h">
        <div className="sklc-ic">
          <Workflow className="ic" aria-hidden />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="sklc-name">{p.name}</div>
          <div className="sklc-slug">{p.slug}</div>
          <div className="skl-badges">
            {p.latest_version_label && (
              <span className="skl-bdg ver">v{p.latest_version_label}</span>
            )}
            <span className={`skl-bdg ${isGlobal ? 'glob' : 'tnt'}`}>{p.scope_label}</span>
            {p.parent_global_id && <span className="skl-bdg">instanciado</span>}
          </div>
        </div>
      </div>

      {/* Descrição (wireframe .sklc-desc) */}
      {p.description && <p className="sklc-desc">{p.description}</p>}

      {/* Switch ativar/desativar (wireframe .pipe-sw-row + .agt-sw) */}
      <div className="pipe-sw-row" onClick={(e) => e.stopPropagation()}>
        <label className="agt-sw">
          <input
            type="checkbox"
            role="switch"
            checked={p.is_active}
            onChange={(e) => onToggleActive(e.target.checked)}
            aria-label={`${p.is_active ? 'Desativar' : 'Ativar'} pipeline ${p.slug}`}
          />
          <span className="sw-track" />
          <span className="sw-lab">{p.is_active ? 'ativado' : 'desativado'}</span>
        </label>
      </div>

      {/* Footer métricas: runs · % sucesso · etapas (wireframe .sklc-foot) */}
      <div className="sklc-foot">
        <span>
          <b>{fmtNumber(totalRuns)}</b> runs
        </span>
        <span title="% sucesso = completos / (completos + falhos); canceladas fora do cálculo">
          <b>{formatSuccessPct(counts)}</b> sucesso
        </span>
        <span>
          <b>{stepsCount != null ? fmtNumber(stepsCount) : '—'}</b>{' '}
          {stepsCount === 1 ? 'etapa' : 'etapas'}
        </span>
      </div>
    </div>
  );
}
